import { format, formatISO } from 'date-fns';
import apiClient from '../shared/apiClient';
import {
  listHierarchyDefinitions,
  hierarchyDefinitionsByClient,
  orgUnitsByClientAndLevel,
  locationByClientId
} from 'graphql/queries';
import {
  createHierarchyDefinition,
  updateHierarchyDefinition
} from 'graphql/mutations';

/**
 * Service for managing hierarchy structure and definitions
 * Copied from locationAdminService.js with minimal changes
 */
class HierarchyService {
  // Get hierarchy definition
  async getHierarchyDefinition(clientId) {
    try {
      console.log('🔍 hierarchyService.getHierarchyDefinition called with clientId:', clientId);

      // Use indexed query for efficient lookup by clientId
      const hierarchyDefResult = await apiClient.query(hierarchyDefinitionsByClient, {
        clientId: clientId,
        sortDirection: 'DESC', // Get highest version first
        filter: {
          endDate: { attributeExists: false } // Get current version (no end date)
        },
        limit: 1
      });

      console.log('📊 hierarchyDefResult:', hierarchyDefResult);

      const hierarchyDef = hierarchyDefResult?.data?.hierarchyDefinitionsByClient?.items?.[0];
      
      if (!hierarchyDef || !hierarchyDef.levels) {
        // Return empty array - no default structure
        return [];
      }
      
      // Build the full hierarchy including Brand and Location
      const fullHierarchy = [];
      
      // Add Brand at top
      fullHierarchy.push({
        levelId: 'BRAND',
        name: 'Brand',
        label: 'Brand',
        isActive: true,
        isUsed: true,
        order: 999,
        icon: 'business'
      });
      
      // Add org levels from definition
      const activeLevels = hierarchyDef.levels
        .sort((a, b) => b.order - a.order);
      
      activeLevels.forEach(level => {
        const levelIcons = {
          'ORGLEVEL_3': 'location_city',     // Highest level
          'ORGLEVEL_2': 'account_tree',      // Middle level  
          'ORGLEVEL_1': 'store_mall_directory' // Closest to stores
        };
        
        fullHierarchy.push({
          levelId: level.levelId,
          name: `Level ${level.order}`,
          label: level.label,
          isActive: true,
          isUsed: level.isUsed !== false,  // Include isUsed field from schema
          order: level.order,
          icon: levelIcons[level.levelId] || 'folder'
        });
      });
      
      // Add Location at bottom
      fullHierarchy.push({
        levelId: 'LOCATION',
        name: 'Location',
        label: 'Location',
        isActive: true,
        isUsed: true,
        order: 0,
        icon: 'store'
      });

      return fullHierarchy;
    } catch (error) {
      console.error('Error fetching hierarchy definition:', error);
      throw error;
    }
  }

  // Get hierarchy structure with counts
  async getHierarchyStructure(clientId) {
    try {
      // First, get the current hierarchy definition
      const hierarchyDefResult = await apiClient.query(listHierarchyDefinitions, { 
        filter: { 
          clientId: { eq: clientId },
          endDate: { attributeExists: false } // Get current version (no end date)
        },
        limit: 1
      });

      const hierarchyDef = hierarchyDefResult?.data?.listHierarchyDefinitions?.items?.[0];
      
      let levels = [];
      if (!hierarchyDef || !hierarchyDef.levels) {
        // For small operators, just use Brand and Location with no intermediate levels
        levels = [];
      } else {
        levels = hierarchyDef.levels;

      }
      
      // Get org units to count how many units exist at each level
      const orgUnitsResult = await apiClient.query(orgUnitsByClientAndLevel, { clientId });
      const orgUnits = orgUnitsResult?.data?.orgUnitsByClientAndLevel?.items || [];
      
      // Count units per level
      const unitCounts = orgUnits.reduce((acc, unit) => {
        acc[unit.orgLevel] = (acc[unit.orgLevel] || 0) + 1;
        return acc;
      }, {});
      
      // Get locations for store level count
      const locationsResult = await apiClient.query(locationByClientId, { clientId, limit: 1000 });
      const locations = locationsResult?.data?.locationByClientId?.items || [];
      
      // Build hierarchy levels array with fixed structure
      const hierarchyLevels = [];
      
      // Always add Brand at the top
      // Brand is conceptual - the entire organization
      hierarchyLevels.push({
        level: 'Top Level',
        name: 'Brand',
        count: 1, // Always 1 brand representing the whole organization
        isActive: true,
        icon: 'business',
        order: 999, // Highest order for sorting
        levelId: 'BRAND'
      });
      
      // Add configured org levels from HierarchyDefinition
      // Filter and sort the levels (only ORGLEVEL_1, ORGLEVEL_2, ORGLEVEL_3)
      const orgLevels = levels
        .filter(level => level.levelId && level.levelId.startsWith('ORGLEVEL'))
        .sort((a, b) => b.order - a.order); // Sort descending so higher levels come first
      
      // Map org levels with proper display
      orgLevels.forEach((level, index) => {
        const levelIcons = {
          'ORGLEVEL_3': 'location_city',     // Highest org level (e.g., Region)
          'ORGLEVEL_2': 'account_tree',      // Middle level (e.g., Area)
          'ORGLEVEL_1': 'store_mall_directory' // Closest to stores (e.g., Market)
        };
        
        // Display level based on the actual order value
        let displayLevel = `Level ${level.order}`;
        
        hierarchyLevels.push({
          level: displayLevel,
          name: level.label,
          count: unitCounts[level.levelId] || 0,
          isActive: true,
          icon: levelIcons[level.levelId] || 'folder',
          order: level.order,
          levelId: level.levelId
        });
      });
      
      // Always add Location at the bottom
      hierarchyLevels.push({
        level: 'Base Level',
        name: 'Location',
        count: locations.length,
        isActive: true,
        icon: 'store',
        order: 0, // Lowest order
        levelId: 'LOCATION'
      });

      return hierarchyLevels;
    } catch (error) {
      console.error('❌ Error fetching hierarchy structure:', error);
      throw error;
    }
  }

  // Get current hierarchy version information
  async getCurrentHierarchyVersion(clientId) {
    try {
      // Use indexed query for efficient lookup by clientId
      const hierarchyDefResult = await apiClient.query(hierarchyDefinitionsByClient, {
        clientId: clientId,
        sortDirection: 'DESC', // Get highest version first
        filter: {
          endDate: { attributeExists: false }
        },
        limit: 1
      });

      const hierarchyDef = hierarchyDefResult?.data?.hierarchyDefinitionsByClient?.items?.[0];
      
      if (hierarchyDef) {
        return {
          version: hierarchyDef.version || 1,
          effectiveDate: hierarchyDef.effectiveDate,
          endDate: hierarchyDef.endDate || null,
          modifiedBy: hierarchyDef.changedBy || 'System',  // Use changedBy field
          modifiedAt: hierarchyDef.updatedAt || hierarchyDef.createdAt
        };
      }
      
      // Return null if no definition found
      return null;
    } catch (error) {
      console.error('Error fetching hierarchy version:', error);
      throw error;
    }
  }

  // Update hierarchy structure (creates a new version)
  async updateHierarchyStructure(clientId, levels, changeReason, userId = 'unknown', userName = 'Unknown User') {
    try {
      // Get current hierarchy definition - the one without an end date
      const currentDefResult = await apiClient.query(listHierarchyDefinitions, { 
        filter: { 
          clientId: { eq: clientId },
          endDate: { attributeExists: false }  // Current version has no end date
        },
        limit: 1
      });

      const currentDef = currentDefResult?.data?.listHierarchyDefinitions?.items?.[0];
      const currentVersion = currentDef?.version || 0;  // Start at 0 if no current version
      const newVersion = currentVersion + 1;  // First version will be 1
      const now = formatISO(new Date());
      
      // Format levels for storage - filter out Brand, Location, and any deleted levels
      const levelsToStore = levels
        .filter(level => {
          const levelId = level.levelId || level.id;
          // Exclude Brand, Location, and any levels marked as deleted/inactive
          return levelId && 
                 levelId !== 'BRAND' && 
                 levelId !== 'LOCATION' && 
                 level.isActive !== false && 
                 !level.isDeleted;
        })
        .map(level => ({
          levelId: level.levelId || level.id,
          label: level.label || `Level ${level.order}`,  // Ensure label is never null
          order: level.order || 1,  // Ensure order is never null
          isUsed: true,  // Required field in GraphQL schema - set to true for all active levels
          introducedInVersion: level.introducedInVersion || newVersion
        }));

      // Create new hierarchy definition
      const createInput = {
        clientId,
        version: newVersion,
        effectiveDate: now,
        levels: levelsToStore,
        changeReason: changeReason || 'Manual update via Location Admin',
        changedBy: userId || 'unknown'
        // Note: changedByName, createdAt, updatedAt are not part of the input type
      };

      let createResult;
      try {
        createResult = await apiClient.mutate(createHierarchyDefinition, { 
          input: createInput 
        });

      } catch (mutationError) {
        console.error('Mutation error:', mutationError);
        console.error('Mutation error details:', JSON.stringify(mutationError, null, 2));
        throw mutationError;
      }
      
      // Update the current definition to set its end date
      if (currentDef?.id) {
        const updateInput = {
          id: currentDef.id,
          endDate: now
          // Note: updatedAt is managed by the system, not part of input
        };

        await apiClient.mutate(updateHierarchyDefinition, {
          input: updateInput
        });
      }
      
      // Create audit log for the change
      const auditData = {
        clientId,
        changeType: 'HIERARCHY_UPDATE',
        entityType: 'HierarchyDefinition',
        entityId: createResult.data.createHierarchyDefinition.id,
        entityName: `Hierarchy v${newVersion}`,
        changeSummary: `Updated hierarchy structure to version ${newVersion}`,
        changeReason,
        changedBy: userId || 'unknown',
        changedByName: userName || 'Unknown User',
        fieldChanges: levelsToStore.map(level => ({
          fieldName: level.levelId,
          newValue: level.label,
          oldValue: currentDef?.levels?.find(l => l.levelId === level.levelId)?.label || '',
          newDisplayValue: level.label,
          oldDisplayValue: currentDef?.levels?.find(l => l.levelId === level.levelId)?.label || ''
        }))
      };
      
      // Import historyService to create audit log
      const { default: historyService } = await import('./historyService');
      await historyService.createAuditLog(auditData);
      
      return {
        success: true,
        newVersion,
        effectiveDate: now,
        hierarchyDefinition: createResult.data.createHierarchyDefinition
      };
    } catch (error) {
      console.error('Error updating hierarchy structure:', error);
      console.error('Error details:', {
        message: error.message,
        errors: error.errors,
        data: error.data,
        stack: error.stack
      });
      throw error;
    }
  }

  // Helper method to get level type label
  getLevelTypeLabel(orgLevel) {
    // Note: Higher number = higher organizational level
    // ORGLEVEL_3 = Region (highest)
    // ORGLEVEL_2 = Area/District
    // ORGLEVEL_1 = Market/Territory (lowest org level above stores)
    const levelMap = {
      'ORGLEVEL_3': 'Region',
      'ORGLEVEL_2': 'Area',
      'ORGLEVEL_1': 'District',
      'BRAND': 'Brand',
      'STORE_LEVEL': 'Store'
    };
    return levelMap[orgLevel] || orgLevel;
  }

  // Helper method to get org level number
  getOrgLevelNumber(orgLevel) {
    const match = orgLevel?.match(/ORGLEVEL_(\d)/);
    return match ? parseInt(match[1]) : 0;
  }
}

export default new HierarchyService();